import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faIdCard, faPhone, faEnvelope, faLocationDot, faMoneyBill, faHashtag, faNoteSticky, faGear, faFileCirclePlus, faTrash, faCodeBranch } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import {
  fetchBranchById,
  createBranch,
  updateBranch,
  uploadBranchAttachmentFiles,
  mapFormToUpdatePayload,
} from '../branchesApi';
import {
  EMPTY_FORM,
  getSampleBranchForm,
  validateBranchForm,
  formatFileSize,
  inferTypeFromName,
} from '../branchFormUtils';
import './BranchesPage.css';
import './DecisionsPage.css';
import './DecisionFormPage.css';

function BranchFormPage({ mode }) {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const invalidEditId = !isNew && (!Number.isFinite(editId) || editId < 1);

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(!isNew && !invalidEditId);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    if (invalidEditId) { setLoadError(t('branches.invalidId')); setLoading(false); return; }
    if (isNew) { setForm({ ...EMPTY_FORM }); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoadError(''); setLoading(true);
      try {
        const item = await fetchBranchById(editId);
        if (cancelled) return;
        setForm({
          name: item.name, crn: item.crn, address: item.address, phone: item.phone, email: item.email,
          registeredCapital: item.registeredCapital, paidInCapital: item.paidInCapital,
          authorizedCapital: item.authorizedCapital, sharesCount: item.sharesCount,
          shareParValue: item.shareParValue, notes: item.notes, status: item.status,
          isMain: !!item.isMain,
          contracts: (item.contracts || []).map((c) => ({ ...c })),
          newAttName: '', newAttType: 'pdf',
        });
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('branches.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, invalidEditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeContract = (idx) => {
    setForm((p) => {
      const c = p.contracts[idx];
      if (c?.url && String(c.url).startsWith('blob:')) { try { URL.revokeObjectURL(c.url); } catch (e) { /* ignore */ } }
      return { ...p, contracts: p.contracts.filter((_, i) => i !== idx) };
    });
  };

  const handleContractFiles = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((p) => {
      const next = [...p.contracts];
      const type = inferTypeFromName(file.name);
      const url = URL.createObjectURL(file);
      next.push({ id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, name: file.name, type, size: formatFileSize(file.size, language), url, __file: file });
      return { ...p, contracts: next };
    });
    e.target.value = '';
  };

  const handleChange = (e) => {
    setErrors((prev) => ({ ...prev, [e.target.name]: '', _api: '' }));
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    const errs = validateBranchForm(form, {
      nameRequired: t('branches.valName'),
      crnRequired: t('branches.valCrn'),
      addressRequired: t('branches.valAddress'),
      phoneRequired: t('branches.valPhone'),
      emailInvalid: t('branches.valEmail'),
    });
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    const pendingFiles = form.contracts.filter((c) => c?.__file).map((c) => c.__file);
    const savedContracts = form.contracts.map(({ id, name, type, size, url, storedFileName }) => ({
      id, name, type, ...(storedFileName ? { storedFileName } : {}), ...(url ? { url } : {}),
    }));
    try {
      const payload = mapFormToUpdatePayload({ ...form, contracts: savedContracts });
      let targetId = editId;
      if (isNew) { const created = await createBranch(payload); targetId = created.id; }
      else { const updated = await updateBranch(editId, payload); targetId = updated.id; }
      if (pendingFiles.length > 0 && targetId) await uploadBranchAttachmentFiles(targetId, pendingFiles);
      navigate('/app/branches');
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('branches.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{isNew ? t('branches.titleNew') : t('branches.titleEdit')}</span>
            <span className="page__topbar-sub">{t('branches.ceaOnly')}</span>
          </div>
        </div>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  const secondaryIconStyle = { color: '#0EA5A0' };
  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {loading && <div className="v2-loading">{t('branches.loading')}</div>}
        {!loading && !loadError && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">
                    <span className="page-title-icon"><FontAwesomeIcon icon={faCodeBranch} style={secondaryIconStyle} /></span>
                    {isNew ? t('branches.titleNew') : t('branches.titleEdit')}
                  </h1>
                </div>
                <div className="action-btns">
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading || !!loadError}>
                    {saving ? t('branches.saving') : isNew ? t('branches.addBranch') : t('branches.saveChanges')}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => navigate('/app/branches')}>{t('common.cancel')}</button>
                  {isNew && <button type="button" className="btn btn-outline" onClick={() => { setErrors({}); setForm(getSampleBranchForm()); }} disabled={saving}>{t('branches.fillSample')}</button>}
                </div>
              </div>
            </div>
            <div className="card-inner">
              {errors._api && <div className="v2-error">{errors._api}</div>}
              <div className="fields-grid">
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faBuilding} style={secondaryIconStyle} /> {t('branches.lblName')} <span className="required">*</span></label><div className="field-input"><input className={errors.name ? 'mform__input--error' : ''} name="name" placeholder={t('branches.lblName')} value={form.name} onChange={handleChange} /></div>{errors.name && <span className="mform__error">{errors.name}</span>}</div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faIdCard} style={secondaryIconStyle} /> {t('branches.lblCrn')} <span className="required">*</span></label><div className="field-input"><input className={errors.crn ? 'mform__input--error' : ''} name="crn" placeholder="e.g. 1010123456" value={form.crn} onChange={handleChange} dir="ltr" /></div>{errors.crn && <span className="mform__error">{errors.crn}</span>}</div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faPhone} style={secondaryIconStyle} /> {t('branches.lblPhone')} <span className="required">*</span></label><div className="field-input"><input className={errors.phone ? 'mform__input--error' : ''} name="phone" placeholder="05xxxxxxxx" value={form.phone} onChange={handleChange} dir="ltr" /></div>{errors.phone && <span className="mform__error">{errors.phone}</span>}</div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faEnvelope} style={secondaryIconStyle} /> {t('branches.lblEmail')}</label><div className="field-input"><input className={errors.email ? 'mform__input--error' : ''} name="email" type="email" placeholder="info@branch.com" value={form.email} onChange={handleChange} dir="ltr" /></div>{errors.email && <span className="mform__error">{errors.email}</span>}</div>
              </div>
              <div className="section-divider" />
              <div className="fields-grid">
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faMoneyBill} style={secondaryIconStyle} /> {t('branches.lblRegisteredCap')}</label><div className="field-input"><input name="registeredCapital" type="number" min="0" step="0.01" value={form.registeredCapital} onChange={handleChange} dir="ltr" /></div></div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faMoneyBill} style={secondaryIconStyle} /> {t('branches.lblPaidInCap')}</label><div className="field-input"><input name="paidInCapital" type="number" min="0" step="0.01" value={form.paidInCapital} onChange={handleChange} dir="ltr" /></div></div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faMoneyBill} style={secondaryIconStyle} /> {t('branches.lblAuthorizedCap')}</label><div className="field-input"><input name="authorizedCapital" type="number" min="0" step="0.01" value={form.authorizedCapital} onChange={handleChange} dir="ltr" /></div></div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faHashtag} style={secondaryIconStyle} /> {t('branches.lblSharesCount')}</label><div className="field-input"><input name="sharesCount" type="number" min="0" step="1" value={form.sharesCount} onChange={handleChange} dir="ltr" /></div></div>
              </div>
              <div className="fields-grid" style={{ marginTop: 20 }}>
                <div className="field-group span-2"><label className="field-label"><FontAwesomeIcon icon={faLocationDot} style={secondaryIconStyle} /> {t('branches.lblAddress')} <span className="required">*</span></label><div className="field-input"><input className={errors.address ? 'mform__input--error' : ''} name="address" value={form.address} onChange={handleChange} /></div>{errors.address && <span className="mform__error">{errors.address}</span>}</div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faMoneyBill} style={secondaryIconStyle} /> {t('branches.lblShareParValue')}</label><div className="field-input"><input name="shareParValue" type="number" min="0" step="0.01" value={form.shareParValue} onChange={handleChange} dir="ltr" /></div></div>
                <div className="field-group"><label className="field-label"><FontAwesomeIcon icon={faGear} style={secondaryIconStyle} /> {t('branches.lblStatus')}</label><div className="field-input select-wrapper"><select name="status" value={form.status} onChange={handleChange}><option value="active">{t('common.active')}</option><option value="inactive">{t('common.inactive')}</option></select></div></div>
                <div className="field-group">
                  <label className="field-label"><FontAwesomeIcon icon={faCodeBranch} style={secondaryIconStyle} /> {t('branches.lblIsMain')}</label>
                  <div className="field-input" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      className={`sa-toggle${form.isMain ? ' sa-toggle--on' : ''}`}
                      onClick={() => setForm((p) => ({ ...p, isMain: !p.isMain }))}
                    >
                      <span className="sa-toggle__thumb" />
                    </button>
                    <span style={{ fontSize: '0.84rem', color: form.isMain ? '#02c3a6' : '#90a4ae', fontWeight: 600 }}>
                      {form.isMain ? t('branches.typeMain') : t('branches.typeSub')}
                    </span>
                    <span style={{ fontSize: '0.76rem', color: '#b0bec5' }}>— {t('branches.isMainHint')}</span>
                  </div>
                </div>
              </div>
              <div className="fields-grid" style={{ marginTop: 20 }}>
                <div className="field-group span-2"><label className="field-label"><FontAwesomeIcon icon={faNoteSticky} style={secondaryIconStyle} /> {t('branches.lblNotes')}</label><div className="field-input"><input name="notes" value={form.notes} onChange={handleChange} /></div></div>
              </div>
            </div>
            <div className="attachments-header">
              <div className="badge">{form.contracts.length}</div>
              <span className="attachments-label"><FontAwesomeIcon icon={faFileCirclePlus} style={secondaryIconStyle} /> {t('branches.sectionContracts')}</span>
            </div>
            <div className="upload-zone" onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}>
              <div className="upload-icon"><FontAwesomeIcon icon={faFileCirclePlus} color="#fff" /></div>
              <div className="upload-title">{t('branches.pickFiles')}</div>
              <div className="upload-sub">{t('branches.hintContracts')}</div>
            </div>
            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" style={{ display: 'none' }} onChange={handleContractFiles} />
            {form.contracts.length > 0 && (
              <div className="v2-att-list">
                {form.contracts.map((att, i) => (
                  <div key={att.id || i} className="v2-att-item">
                    <span className="v2-att-type">{String(att.type).toUpperCase()}</span>
                    <span className="v2-att-name" title={att.name}>{att.name}</span>
                    <span className="v2-att-size">{att.size}</span>
                    <button type="button" className="v2-att-remove" onClick={() => removeContract(i)} title={t('common.delete')}><FontAwesomeIcon icon={faTrash} color="#c62828" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BranchFormPage;
